/**
 * @file        src/api/queryKeys.js
 * @description Central registry of React Query keys.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *  - 2026-10-09 Claude — Added seo (SEO Management: settings, pages, one page,
 *                         preview, redirects, audit, robots, sitemap). Every key
 *                         carries the brand — SEO is per storefront.
 *  - 2026-09-24 Claude — Added actionCenter (overview + items) and activityLog
 *                         (list + families). actionCenter.root exists so one
 *                         close can invalidate BOTH the page's list and every
 *                         dashboard panel — they read the same counts, and a
 *                         panel still showing an item somebody just finished is
 *                         the same disagreement the shared query was built to
 *                         prevent.
 *  - 2026-09-22 Claude — Added the paymentReconciliation keys (list + totals
 *                         + reasons).
 *  - 2026-09-22 Claude — Added the abandonedPayments keys (list + totals).
 *   - 2026-09-15 Claude — Dropped the homeSlider keys; Site Management → Home
 *                          Slider Image was removed as unused scaffolding (its
 *                          rows never reached either storefront).
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-30 Claude — Added dashboard.walletBalance + tenants.supplierCredentials.
 *   - 2026-08-04 Claude — Added faqs (FAQ Management, CR A2).
 *   - 2026-08-04 Claude — Added tenants.branding (Admin Portal Branding panel).
 *   - 2026-09-25 Claude — Added groupRequests (Query Management → Group
 *                          Booking Requests).
 *   - 2026-08-04 Claude — Added enquiries (CR A5: Query Management → Query List).
 *   - 2026-08-04 Claude — Added businessAccounts (Customer List B2B tab).
 *   - 2026-08-04 Claude — Added invoiceSeries (CR B6: Invoice Number Series).
 *   - 2026-08-04 Claude — Added creditDues (B2B post-pay Credit Dues admin UI).
 *   - 2026-08-05 Claude — Added emailSettings (per-brand SMTP settings +
 *                          lightweight status feed for the topbar pill/banner).
 *   - 2026-08-05 Claude — Added communicationLogs (Account Management →
 *                          Communication Log: outbound email attempt viewer).
 */
export const qk = {
  me: ['me'],
  brands: {
    list: ['brands', 'list'],
  },
  emailSettings: {
    list: ['email-settings', 'list'],
    status: ['email-settings', 'status'],
  },
  communicationLogs: {
    list: (params) => ['communication-logs', 'list', params],
    detail: (id) => ['communication-logs', 'detail', id],
  },
  invoiceSeries: {
    list: ['invoice-series', 'list'],
  },
  actionCenter: {
    root: ['action-center'],
    overview: (audience) => ['action-center', 'overview', audience || 'all'],
    items: (params) => ['action-center', 'items', params],
  },
  activityLog: {
    list: (params) => ['activity-log', 'list', params],
    families: ['activity-log', 'families'],
  },
  dashboard: {
    summary: ['dashboard', 'summary'],
    recent: ['dashboard', 'recent'],
    series: ['dashboard', 'series'],
    // Shared by the Cash Balance card and the Topbar pill, so the live supplier
    // balance is fetched ONCE per load and both render the same number.
    walletBalance: ['dashboard', 'wallet-balance'],
  },
  airports: {
    list: (params) => ['airports', 'list', params],
    one: (id) => ['airports', 'one', id],
  },
  packageCategories: {
    list: (params) => ['package-categories', 'list', params],
    one: (id) => ['package-categories', 'one', id],
  },
  packages: {
    list: (params) => ['packages', 'list', params],
    one: (id) => ['packages', 'one', id],
  },
  holidays: {
    list: (params) => ['holidays', 'list', params],
    one: (id) => ['holidays', 'one', id],
  },
  blogCategories: {
    list: (params) => ['blog-categories', 'list', params],
    one: (id) => ['blog-categories', 'one', id],
  },
  blogs: {
    list: (params) => ['blogs', 'list', params],
    one: (id) => ['blogs', 'one', id],
  },
  offers: {
    list: (params) => ['offers', 'list', params],
    one: (id) => ['offers', 'one', id],
    // Keyed by service: a flight offer and a hotel offer redeem different coupons.
    couponOptions: (serviceFor) => ['offers', 'coupon-options', serviceFor],
  },
  customers: {
    list: (params) => ['customers', 'list', params],
    one: (id) => ['customers', 'one', id],
  },
  staff: {
    list: (params) => ['staff', 'list', params],
    one: (id) => ['staff', 'one', id],
  },
  businessAccounts: {
    list: (params) => ['business-accounts', 'list', params],
    one: (id) => ['business-accounts', 'one', id],
    employees: (id, params) => ['business-accounts', 'employees', id, params],
  },
  creditDues: {
    list: (params) => ['credit-dues', 'list', params],
  },
  paymentGateways: {
    list: (params) => ['payment-gateways', 'list', params],
    one: (id) => ['payment-gateways', 'one', id],
  },
  taxSettings: { get: ['tax-settings'] },
  testimonials: {
    list: (params) => ['testimonials', 'list', params],
    one: (id) => ['testimonials', 'one', id],
  },
  faqs: {
    list: (params) => ['faqs', 'list', params],
    one: (id) => ['faqs', 'one', id],
  },
  enquiries: {
    list: (params) => ['enquiries', 'list', params],
    one: (id) => ['enquiries', 'one', id],
  },
  // Query Management → Group Booking Requests. The list key carries the KPI
  // totals too — one query feeds both (see api/groupRequests.js).
  groupRequests: {
    list: (params) => ['group-requests', 'list', params],
    one: (id) => ['group-requests', 'one', id],
  },
  suppliers: { list: () => ['suppliers', 'list'] },
  siteSettings: {
    get: ['site-settings'],
    // Issuing company for printed documents — its own key because it comes from
    // a separate endpoint that is NOT module-gated (see lib/useCompany.js).
    company: ['site-settings', 'company'],
    publicBranding: ['site-settings', 'public-branding'],
  },
  abandonedPayments: {
    list: (params) => ['abandoned-payments', 'list', params],
    totals: (params) => ['abandoned-payments', 'totals', params],
  },
  paymentReconciliation: {
    list: (params) => ['payment-reconciliation', 'list', params],
    totals: (params) => ['payment-reconciliation', 'totals', params],
    reasons: () => ['payment-reconciliation', 'reasons'],
  },
  transactions: {
    list: (params) => ['transactions', 'list', params],
    totals: (params) => ['transactions', 'totals', params],
    balanceSheet: (params) => ['transactions', 'balance-sheet', params],
  },
  wallet: {
    users: (params) => ['wallet', 'users', params],
    detail: (userId) => ['wallet', 'detail', userId],
    transactions: (userId, params) => ['wallet', 'transactions', userId, params],
    settings: ['wallet', 'settings'],
  },
  tenants: {
    list: (params) => ['tenants', 'list', params],
    one: (id) => ['tenants', 'one', id],
    supplierCredentials: (id) => ['tenants', 'supplier-credentials', id],
    branding: (id) => ['tenants', 'branding', id],
    company: (id) => ['tenants', 'company', id],
    readiness: (id) => ['tenants', 'readiness', id],
    brands: (id) => ['tenants', 'brands', id],
    paymentGateways: (id) => ['tenants', 'payment-gateways', id],
    supplierLogs: (id, params) => ['tenants', 'supplier-logs', id, params],
    supplierLog: (id, logId) => ['tenants', 'supplier-logs', id, logId],
  },
  // SEO Management. `seo.root` lets one save invalidate every SEO screen: a
  // settings change moves every page's resolved title, preview and audit row.
  seo: {
    root: ['seo'],
    settings: (brand) => ['seo', 'settings', brand],
    pages: (brand) => ['seo', 'pages', brand],
    page: (brand, key) => ['seo', 'page', brand, key],
    preview: (body) => ['seo', 'preview', body],
    redirects: (params) => ['seo', 'redirects', params],
    audit: (brand) => ['seo', 'audit', brand],
    robots: (brand) => ['seo', 'robots', brand],
    sitemap: (brand) => ['seo', 'sitemap', brand],
  },
  platformAdminUsers: {
    list: (params) => ['platform-admin-users', 'list', params],
    one: (id) => ['platform-admin-users', 'one', id],
  },
};
